import {
  getAccessToken,
  endRoom,
  sendLivestreamStartEmails,
  startEgress,
  startStreamDestination,
  stopStreamDestination,
} from './api';
import { createContext, PropsWithChildren, useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { LivestreamSession, LivestreamSessionStreamDestination } from 'cb-model';
import { notify, useCacheQuery, useCacheRecord } from 'gossamer-front-end';
interface LivestreamSessionContextValue {
  roomId: string;
  isPreview: boolean;
  token: string;
  recordingProgressStatus: 'IDLE' | 'IN_PROGRESS' | 'LOADING' | 'FAILED';
  handleLeaveRoom: () => void;
  handleRoomConnection: () => Promise<void>;
  handleEndLivestream: () => Promise<void>;
  handleStartRecording: () => Promise<void>;
  // Add streaming related states and handlers
  streamingProgressStatus: 'IDLE' | 'IN_PROGRESS' | 'LOADING' | 'FAILED' | 'PARTIALLY_FAILED';
  handleStartStreaming: () => Promise<void>;
  handleStopStreaming: () => Promise<void>;
  hasStreamingDestinations: boolean;
  streamingStopFailed: boolean;
}

export const LivestreamSessionContext = createContext<LivestreamSessionContextValue | null>(null);

interface RouteParams extends Record<string, string | undefined> {
  roomId?: string;
}
export const LivestreamSessionProvider = (props: PropsWithChildren) => {
  const { roomId } = useParams<RouteParams>();
  const location = useLocation();
  const isPreview = location.pathname.endsWith('/preview');
  const navigate = useNavigate();

  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [isJoined, setIsJoined] = useState(false);
  const [recordingProgressStatus, setRecordingProgressStatus] = useState<'IDLE' | 'IN_PROGRESS' | 'LOADING' | 'FAILED'>(
    'IDLE',
  );
  const [streamingProgressStatus, setStreamingProgressStatus] = useState<
    'IDLE' | 'IN_PROGRESS' | 'LOADING' | 'FAILED' | 'PARTIALLY_FAILED'
  >('IDLE');
  const [streamingStopFailed, setStreamingStopFailed] = useState(false);

  const livestreamSession = useCacheRecord(LivestreamSession, roomId || '', !roomId);

  const streamingDestinationsQuery = useCacheQuery(LivestreamSessionStreamDestination, {
    where: { and: [{ fieldId: 'livestream_session_id', operator: 'eq', value: livestreamSession?.key }] },
    limit: 10, // max number of streaming destinations that can be used
  });

  const hasStreamingDestinations = (streamingDestinationsQuery.data?.length ?? 0) > 0;

  useEffect(() => {
    // If no roomId, go back to lobby
    if (!roomId) {
      navigate('/dashboard/hosted-events');
      return;
    }

    // Fetch the host token on mount
    (async () => {
      try {
        const [response, responseStatusCode] = await getAccessToken(roomId, 'host', isPreview);

        if (responseStatusCode === 401 || response.message === 'unauthorised') {
          setError(
            'You must be logged in to host a livestream! After logging in, you will be taken back to host the livestream. Redirecting to login...',
          );
          window.setTimeout(() => {
            const redirectTo = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.href = `/auth/login?redirectToPath=${redirectTo}`;
          }, 3000);
          return;
        }

        if (!response.token) {
          throw new Error(response.error || 'Failed to get host access token.');
        }
        setToken(response.token);
        setIsJoined(true);
      } catch (err) {
        console.error('Error joining room:', err);
        setError((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to join room.');
      }
    })();
  }, [isPreview, navigate, roomId]);

  async function handleRoomConnection() {
    if (isPreview || !roomId) return;
    try {
      const [, responseStatusCode] = await sendLivestreamStartEmails(roomId);
      if (responseStatusCode !== 200) {
        console.error('Could not send emails, check logs for more info');
      }
    } catch (error) {
      console.error(error);
    }
  }

  function handleLeaveRoom() {
    setIsJoined(false);
    setToken('');
    window.location.href = '/admin/event/search';
  }

  async function handleEndLivestream() {
    if (!roomId) return;
    try {
      if (!isPreview) {
        const [response, responseStatusCode] = await endRoom(roomId);
        if (responseStatusCode < 200 || responseStatusCode >= 300) {
          throw new Error(response.error || response.message || 'Unable to end the livestream. Please try again.');
        }
      }
      handleLeaveRoom();
    } catch (err) {
      console.error('Error ending room:', err);
      notify([err instanceof Error ? err.message : 'Unable to end the livestream. Please try again.', 'danger']);
    }
  }

  async function handleStartRecording() {
    setRecordingProgressStatus('LOADING');
    if (!roomId) {
      return;
    }
    const [response, responseStatusCode] = await startEgress(roomId);
    if (responseStatusCode == 200) {
      switch ((response as { status?: string })?.status) {
        case 'ALREADY_STARTED':
          setRecordingProgressStatus('IN_PROGRESS');
          notify(['Recording is already in progress', 'info']);
          break;
        case 'FAILED':
          setRecordingProgressStatus('FAILED');
          notify(['Recording failed to start', 'danger']);
          break;
        case 'STARTED':
          setRecordingProgressStatus('IN_PROGRESS');
          notify(['Recording started successfully', 'success']);
          break;
      }
    } else {
      setRecordingProgressStatus('FAILED');
      notify(['Recording failed to start', 'danger']);
    }
  }

  async function handleStartStreaming() {
    setStreamingStopFailed(false);
    setStreamingProgressStatus('LOADING');
    try {
      if (hasStreamingDestinations) {
        // Start streaming to all destinations in parallel
        const results = await Promise.all(
          streamingDestinationsQuery.data!.map((dest) => startStreamDestination({ destinationId: dest.id })),
        );
        // Check if any of the stream starts failed
        const anyFailed = results.some(({ status }) => status !== 200);
        if (anyFailed) {
          setStreamingProgressStatus('PARTIALLY_FAILED');
          // Show toast for each failed destination
          results.forEach(({ status, error }, index) => {
            if (status !== 200) {
              const errorMessage = error || 'Failed to start streaming to destination';
              const destination = streamingDestinationsQuery.data![index];
              notify([`${errorMessage} (${destination.platform} - ${destination.url})`, 'danger']);
            }
          });
          return;
        } else {
          setStreamingProgressStatus('IN_PROGRESS');
          setRecordingProgressStatus('IN_PROGRESS'); // since streaming relies on egress/recording to be started, we can set recording status to in progress as well
          notify(['Streaming started successfully', 'success']);
        }
      } else {
        notify(['No streaming destinations configured', 'danger']);
        setStreamingProgressStatus('FAILED');
      }
    } catch (err) {
      console.error('Error starting streaming:', err);
      setStreamingProgressStatus('FAILED');
    }
  }

  async function handleStopStreaming() {
    setStreamingProgressStatus('LOADING');
    try {
      if (hasStreamingDestinations) {
        // Stop streaming to all destinations in parallel
        const results = await Promise.allSettled(
          streamingDestinationsQuery.data!.map((dest) => stopStreamDestination({ destinationId: dest.id })),
        );
        let failures = 0;
        results.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value.status >= 200 && result.value.status < 300) return;
          failures++;
          const destination = streamingDestinationsQuery.data![index];
          let errorMessage: string;
          if (result.status === 'rejected') {
            console.error('Error stopping streaming destination:', result.reason);
            errorMessage =
              result.reason instanceof Error ? result.reason.message : 'Failed to stop streaming to destination';
          } else {
            errorMessage = result.value.error || result.value.message || 'Failed to stop streaming to destination';
          }
          notify([`${errorMessage} (${destination.platform} - ${destination.url})`, 'danger']);
        });
        if (failures > 0) {
          setStreamingStopFailed(true);
          setStreamingProgressStatus(failures === results.length ? 'FAILED' : 'PARTIALLY_FAILED');
          return;
        }
        setStreamingStopFailed(false);
        setStreamingProgressStatus('IDLE');
        notify(['Streaming stopped successfully', 'success']);
      } else {
        notify(['No streaming destinations configured', 'danger']);
        setStreamingProgressStatus('FAILED');
      }
    } catch (err) {
      console.error('Error stopping streaming:', err);
      setStreamingStopFailed(true);
      setStreamingProgressStatus('FAILED');
      notify([err instanceof Error ? err.message : 'Unable to stop streaming. Please try again.', 'danger']);
    }
  }

  const value = useMemo(() => {
    return {
      roomId,
      token,
      isPreview: !!isPreview,
      isJoined,
      recordingProgressStatus,
      handleLeaveRoom,
      handleRoomConnection,
      handleEndLivestream,
      handleStartRecording,
      hasStreamingDestinations,
      streamingProgressStatus,
      streamingStopFailed,
      handleStartStreaming,
      handleStopStreaming,
    };
  }, [
    roomId,
    token,
    isPreview,
    isJoined,
    recordingProgressStatus,
    hasStreamingDestinations,
    streamingProgressStatus,
    streamingStopFailed,
  ]);

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>;
  }

  if (!isJoined) {
    return <div>Connecting as host...</div>;
  }

  return (
    <LivestreamSessionContext.Provider value={{ ...value, roomId: roomId || '' }}>
      {props.children}
    </LivestreamSessionContext.Provider>
  );
};
